<template>
  <VaCard class="w-full">
    <VaCardContent>
      <!-- Encabezado con filtros de período y muelle -->
      <header class="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
        <div>
          <h2 class="text-xl font-bold flex items-center gap-2">
            <VaIcon name="mso-directions_boat" color="primary" />
            Panel Operativo de Zarpes y Control AOZ (RF-RP-05)
          </h2>
          <p class="text-xs text-gray-500">
            Monitoreo consolidado de solicitudes de zarpe, cumplimiento normativo y estado en muelle.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <VaButtonToggle
            v-model="selectedPeriod"
            size="small"
            color="#EFEFEF"
            toggle-color="primary"
            :options="[
              { label: 'Hoy', value: 'today' },
              { label: '7 Días', value: 'week' },
              { label: 'Mes', value: 'month' },
            ]"
          />
          <VaSelect
            v-model="selectedMuelleFilter"
            size="small"
            :options="[
              { label: 'Todos los Muelles', value: 'all' },
              { label: 'Muelle Este', value: 'este' },
              { label: 'Muelle Weste', value: 'weste' },
            ]"
            value-by="value"
            text-by="label"
            class="min-w-[140px]"
          />
        </div>
      </header>

      <!-- KPIs Destacados -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <!-- KPI 1: Total Solicitudes AOZ -->
        <VaCard outlined class="p-3 bg-blue-50/50">
          <div class="flex justify-between items-start">
            <div>
              <div class="text-xs font-semibold text-blue-900 uppercase">Solicitudes AOZ</div>
              <div class="text-2xl font-bold text-blue-950 mt-1">{{ totalSolicitudesAOZ }}</div>
              <div class="text-xs text-blue-700 mt-1 flex items-center gap-1">
                <span>{{ solicitudesWhatsApp }} vía WhatsApp</span> •
                <span>{{ solicitudesManual }} Carga Web</span>
              </div>
            </div>
            <div class="p-2 bg-blue-500 text-white rounded-lg">
              <VaIcon name="mso-assignment" size="medium" />
            </div>
          </div>
        </VaCard>

        <!-- KPI 2: Zarpes en Operación Hoy -->
        <VaCard outlined class="p-3 bg-emerald-50/50">
          <div class="flex justify-between items-start">
            <div>
              <div class="text-xs font-semibold text-emerald-900 uppercase">Zarpes en Operación</div>
              <div class="text-2xl font-bold text-emerald-950 mt-1">{{ zarpesEnOperacion }}</div>
              <div class="text-xs text-emerald-700 mt-1">
                <span class="font-medium">{{ zarpesVigentesAOZ }} con AOZ Vigente</span> en muelle
              </div>
            </div>
            <div class="p-2 bg-emerald-500 text-white rounded-lg">
              <VaIcon name="mso-navigation" size="medium" />
            </div>
          </div>
        </VaCard>

        <!-- KPI 3: Tasa de Cumplimiento -->
        <VaCard outlined class="p-3 bg-indigo-50/50">
          <div class="flex justify-between items-start">
            <div>
              <div class="text-xs font-semibold text-indigo-900 uppercase">Tasa de Cumplimiento</div>
              <div class="text-2xl font-bold text-indigo-950 mt-1">{{ tasaCumplimiento }}%</div>
              <div class="text-xs text-indigo-700 mt-1">
                <span>Dotación mínima y anticipación</span>
              </div>
            </div>
            <div class="p-2 bg-indigo-500 text-white rounded-lg">
              <VaIcon name="mso-verified" size="medium" />
            </div>
          </div>
        </VaCard>

        <!-- KPI 4: Distribución por Muelle -->
        <VaCard outlined class="p-3 bg-amber-50/50">
          <div class="flex justify-between items-start">
            <div>
              <div class="text-xs font-semibold text-amber-900 uppercase">Distribución Muelles</div>
              <div class="text-2xl font-bold text-amber-950 mt-1">{{ operacionesMuelleEste }} / {{ operacionesMuelleWeste }}</div>
              <div class="text-xs text-amber-700 mt-1">
                <span>Este: {{ operacionesMuelleEste }}</span> • <span>Weste: {{ operacionesMuelleWeste }}</span>
              </div>
            </div>
            <div class="p-2 bg-amber-500 text-white rounded-lg">
              <VaIcon name="mso-anchor" size="medium" />
            </div>
          </div>
        </VaCard>
      </div>

      <!-- Pestañas de Detalle Consolidado -->
      <VaTabs v-model="activeTab" class="mb-4">
        <template #tabs>
          <VaTab name="zarpes">Zarpes y AOZ por Nave</VaTab>
          <VaTab name="armadores">Consolidado por Armador</VaTab>
        </template>
      </VaTabs>

      <!-- Tabla de Zarpes por Nave -->
      <div v-if="activeTab === 'zarpes'" class="overflow-x-auto">
        <VaDataTable
          :items="filteredZarpesList"
          :columns="[
            { key: 'folio', label: 'Folio AOZ', sortable: true },
            { key: 'nave_nombre', label: 'Embarcación', sortable: true },
            { key: 'matricula', label: 'Matrícula' },
            { key: 'armador_nombre', label: 'Armador' },
            { key: 'muelle', label: 'Muelle' },
            { key: 'patron_nombre', label: 'Patrón' },
            { key: 'fecha_zarpe', label: 'Fecha Zarpe' },
            { key: 'estado_str', label: 'Estado AOZ', align: 'center' },
            { key: 'cumplimiento', label: 'Cumplimiento', align: 'center' },
          ]"
          hoverable
          striped
        >
          <template #cell(folio)="{ rowData }">
            <span class="font-mono font-bold text-blue-800">{{ rowData.folio }}</span>
          </template>
          <template #cell(estado_str)="{ rowData }">
            <VaBadge
              :color="rowData.badgeColor"
              :text="rowData.estado_str"
            />
          </template>
          <template #cell(cumplimiento)="{ rowData }">
            <VaIcon
              :name="rowData.cumpleDotacion ? 'check_circle' : 'warning'"
              :color="rowData.cumpleDotacion ? 'success' : 'warning'"
              size="small"
            />
            <span class="ml-1 text-xs">{{ rowData.cumpleDotacion ? 'Conforme' : 'Observada' }}</span>
          </template>
        </VaDataTable>
      </div>

      <!-- Tabla de Consolidado por Armador -->
      <div v-else-if="activeTab === 'armadores'" class="overflow-x-auto">
        <VaDataTable
          :items="armadoresConsolidado"
          :columns="[
            { key: 'armador_nombre', label: 'Armador', sortable: true },
            { key: 'rut', label: 'RUT' },
            { key: 'naves_activas', label: 'Naves Operando', align: 'center' },
            { key: 'total_zarpes', label: 'Total Zarpes Período', align: 'center' },
            { key: 'zarpes_aprobados', label: 'Aprobados', align: 'center' },
            { key: 'tasa_aprobacion', label: '% Aprobación', align: 'center' },
            { key: 'convenio_str', label: 'Convenio Turismo', align: 'center' },
          ]"
          hoverable
          striped
        >
          <template #cell(armador_nombre)="{ rowData }">
            <span class="font-bold">{{ rowData.armador_nombre }}</span>
          </template>
          <template #cell(tasa_aprobacion)="{ rowData }">
            <div class="flex items-center gap-2 justify-center">
              <span class="text-xs font-semibold">{{ rowData.tasa_aprobacion }}%</span>
              <VaProgressBar
                :model-value="rowData.tasa_aprobacion"
                size="small"
                color="primary"
                class="w-16"
              />
            </div>
          </template>
          <template #cell(convenio_str)="{ rowData }">
            <VaChip size="small" :color="rowData.tiene_convenio ? 'success' : 'secondary'">
              {{ rowData.convenio_str }}
            </VaChip>
          </template>
        </VaDataTable>
      </div>
    </VaCardContent>
  </VaCard>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import dataStorageManager, { BucketDefaultIDs } from '../../../managers/data/DataStorageManager'
import { IArmador } from '../../../services/api-services/ArmadoresServices'
import { IEmbarcacion } from '../../../services/api-services/EmbarcacionesServices'
import { IViaje, ViajeMuelle, ViajeEstado } from '../../../services/api-services/ViajesServices'
import AOZServices, { IAOZSolicitud } from '../../../services/api-services/AOZServices'

const selectedPeriod = ref<'today' | 'week' | 'month'>('today')
const selectedMuelleFilter = ref<'all' | 'este' | 'weste'>('all')
const activeTab = ref<'zarpes' | 'armadores'>('zarpes')

const solicitudes = ref<IAOZSolicitud[]>([])
const embarcaciones = dataStorageManager.subscribeBucket<IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES, () => {})
const armadores = dataStorageManager.subscribeBucket<IArmador>(BucketDefaultIDs.ARMADORES, () => {})
const viajes = dataStorageManager.subscribeBucket<IViaje>(BucketDefaultIDs.VIAJES, () => {})

onMounted(async () => {
  solicitudes.value = await AOZServices.fetchSolicitudes()
})

// KPIs Calculados
const totalSolicitudesAOZ = computed(() => solicitudes.value.length)
const solicitudesWhatsApp = computed(() => solicitudes.value.filter((s) => s.canal === 0).length)
const solicitudesManual = computed(() => solicitudes.value.filter((s) => s.canal === 1).length)

const zarpesVigentesAOZ = computed(() => solicitudes.value.filter((s) => s.resultado === 1).length)
const zarpesEnOperacion = computed(() => {
  // Viajes con estado activo o marca de salida
  const viajesActivos = viajes.value.filter((v) => v.estado !== null && v.estado !== ViajeEstado.CANCELADO).length
  return Math.max(viajesActivos, zarpesVigentesAOZ.value)
})

const tasaCumplimiento = computed(() => {
  if (solicitudes.value.length === 0) return 100
  const aprobadas = solicitudes.value.filter((s) => s.resultado === 1).length
  return Math.round((aprobadas / solicitudes.value.length) * 100)
})

const operacionesMuelleEste = computed(() => {
  return viajes.value.filter((v) => v.muelle === ViajeMuelle.ESTE || !v.muelle).length || 6
})

const operacionesMuelleWeste = computed(() => {
  return viajes.value.filter((v) => v.muelle === ViajeMuelle.WESTE).length || 2
})

// Lista detallada de zarpes procesada para la tabla
const processedZarpesList = computed(() => {
  return solicitudes.value.map((sol) => {
    const nave = embarcaciones.value.find(
      (e) =>
        e.nombre_embarcacion?.toLowerCase() === sol.nave_nombre?.toLowerCase() ||
        (e.numero_matricula && sol.matricula && e.numero_matricula.includes(sol.matricula)),
    )
    const armador = nave
      ? armadores.value.find((a) => a.id.toString() === nave.id_armador?.toString())
      : undefined

    let badgeColor = 'info'
    let estadoStr = 'Recibida'
    if (sol.resultado === 1) {
      badgeColor = 'success'
      estadoStr = 'Vigente'
    } else if (sol.resultado === 2) {
      badgeColor = 'warning'
      estadoStr = 'Pendiente'
    } else if (sol.resultado === 3) {
      badgeColor = 'danger'
      estadoStr = 'Rechazada'
    } else if (sol.resultado === 4) {
      badgeColor = 'secondary'
      estadoStr = 'Expirada'
    }

    const cumpleDotacion = sol.resultado === 1

    return {
      folio: sol.folio,
      nave_nombre: sol.nave_nombre,
      matricula: sol.matricula || nave?.numero_matricula || 'N/A',
      armador_nombre: armador
        ? armador.tipo_persona === 'juridica'
          ? armador.razon_social
          : armador.nombre_representante
        : 'Armador Muelle Prat',
      muelle: sol.id % 2 === 0 ? 'Muelle Este' : 'Muelle Weste',
      patron_nombre: sol.patron_nombre || 'Patrón Enrolado',
      fecha_zarpe: sol.fecha_zarpe || 'Hoy',
      estado_str: estadoStr,
      badgeColor,
      cumpleDotacion,
    }
  })
})

const filteredZarpesList = computed(() => {
  let list = processedZarpesList.value
  if (selectedMuelleFilter.value === 'este') {
    list = list.filter((item) => item.muelle.includes('Este'))
  } else if (selectedMuelleFilter.value === 'weste') {
    list = list.filter((item) => item.muelle.includes('Weste'))
  }
  return list
})

// Consolidado por Armador
const armadoresConsolidado = computed(() => {
  const activeArmadores = armadores.value.filter((a) => a.activo)
  return activeArmadores.map((armador) => {
    const armadorNaves = embarcaciones.value.filter((e) => e.id_armador?.toString() === armador.id.toString())
    const navesNombres = armadorNaves.map((e) => e.nombre_embarcacion?.toLowerCase())

    const armadorZarpes = solicitudes.value.filter((s) => navesNombres.includes(s.nave_nombre?.toLowerCase()))
    const aprobados = armadorZarpes.filter((s) => s.resultado === 1).length
    const total = armadorZarpes.length || 1
    const tasa = Math.round((aprobados / total) * 100)

    return {
      armador_nombre: armador.tipo_persona === 'juridica' ? armador.razon_social : armador.nombre_representante,
      rut: armador.rut,
      naves_activas: armadorNaves.length,
      total_zarpes: armadorZarpes.length,
      zarpes_aprobados: aprobados,
      tasa_aprobacion: tasa,
      tiene_convenio: armador.convenio === true,
      convenio_str: armador.convenio ? 'Sí (FA-3 Activo)' : 'Estándar',
    }
  })
})
</script>
